import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Compass,
  Star,
  Briefcase,
  Trophy,
  Users,
  Target,
  TrendingUp,
  ArrowDown,
  Check,
} from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import { COURSES_LIST, PACKAGES } from "@/lib/data";

const ROADMAP = [
  {
    step: "Step 1",
    title: "Learn High-Income Skills",
    desc: "Master modern, in-demand skills used by working professionals.",
    icon: Sparkles,
  },
  {
    step: "Step 2",
    title: "Build Your Portfolio",
    desc: "Ship real projects that prove your value to the market.",
    icon: Briefcase,
  },
  {
    step: "Step 3",
    title: "Create Your Personal Brand",
    desc: "Position yourself as the obvious choice in your niche.",
    icon: Star,
  },
  {
    step: "Step 4",
    title: "Get Clients & Opportunities",
    desc: "Convert your brand and portfolio into paid work, roles and gigs.",
    icon: Users,
  },
  {
    step: "Step 5",
    title: "Scale Your Income",
    desc: "Compound results into sustainable, location-free income.",
    icon: TrendingUp,
  },
];

const SKILLS = [
  "Modern video editing & motion",
  "Personal brand & positioning",
  "Freelance pricing & client systems",
  "Short-form & long-form content",
  "Design systems & visual taste",
  "Prompting, agents & AI workflows",
  "Sales, outreach & proposals",
  "Portfolio that converts",
];

const CAREERS = [
  "Independent Freelancer",
  "Content Creator / Solopreneur",
  "Video Editor (in-house or agency)",
  "Brand & Identity Designer",
  "AI Power-User / Automation Specialist",
  "Personal Brand Coach",
  "Digital Marketing Specialist",
  "Multi-skill Modern Operator",
];

const OUTCOMES = [
  "Confidence in shipping work that earns",
  "A standout portfolio with real projects",
  "A personal brand that compounds attention",
  "A repeatable client/gig acquisition system",
  "A sustainable, scalable digital income",
];

const PROGRAM_FAQS = [
  {
    q: "What is the Super Profile Program?",
    a: "A complete skill-development ecosystem with 5 premium courses, community, mentorship and continuous support — designed to take you from learning to earning.",
  },
  {
    q: "Do I have to buy the program as a whole?",
    a: "No — you choose a package. E-Lite includes any 2 courses, Basic includes 3 courses plus 2 E-Lite courses free (5 in total), Premium unlocks all 10 plus mentorship, the 1% Community Club and the full ecosystem.",
  },
  {
    q: "Which package is right for me?",
    a: "If you want one focused skill — pick E-Lite. If you want to combine 3 skills with mentorship — Basic. If you want full access and the best outcomes — Premium.",
  },
  {
    q: "How is the program delivered?",
    a: "Structured lessons + weekly assignments + peer reviews + live guidance + mentor support inside our community.",
  },
  {
    q: "Will I really make income?",
    a: "Income depends on your effort and consistency. The program gives you the skills, frameworks and accountability — execution is on you, with our support.",
  },
];

import { useState } from "react";
import { startCheckout } from "@/lib/checkout";

function inr(n) {
  return `₹${n.toLocaleString("en-IN")}`;
}

function PackagePill({ pkg, onBuy }) {
  const isP = pkg.highlight;
  return (
    <div
      data-testid={`sp-package-${pkg.id}`}
      className={`relative rounded-3xl overflow-hidden border ${
        isP
          ? "border-[#06B6D4]/40 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#06B6D4] text-white shadow-[0_20px_60px_-25px_rgba(6,182,212,0.45)]"
          : "border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04]"
      } p-7 hairline`}
    >
      {isP && (
        <div className="absolute top-4 left-4 flex gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full bg-white/15 backdrop-blur px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] font-semibold text-white border border-white/20">
            Most Popular
          </span>
          <span className="inline-flex rounded-full bg-[#06B6D4] text-[#0F172A] px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] font-semibold">
            Best Value
          </span>
        </div>
      )}
      <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${isP ? "text-[#06B6D4] mt-8" : "text-[#2563EB] dark:text-[#06B6D4]"}`}>
        Access Level
      </div>
      <h3 className={`mt-2 font-display text-2xl tracking-tight font-semibold ${isP ? "text-white" : "text-[#0F172A] dark:text-white"}`}>
        {pkg.name}
      </h3>
      <div className={`mt-1 text-sm ${isP ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}>
        {pkg.courseAccess}
      </div>
      <div className="mt-5 flex items-end gap-2">
        <span className={`text-sm line-through ${isP ? "text-slate-300/80" : "text-slate-400"}`}>{inr(pkg.originalPrice)}</span>
        <span className={`font-display text-3xl font-semibold tracking-tighter ${isP ? "text-white" : "text-[#0F172A] dark:text-white"}`}>
          {inr(pkg.offerPrice)}
        </span>
      </div>
      <button
        data-testid={`sp-package-cta-${pkg.id}`}
        onClick={() => onBuy(pkg)}
        className={`mt-6 inline-flex w-full justify-center items-center gap-2 rounded-full px-5 py-3 text-sm font-medium ${
          isP ? "bg-white text-[#0F172A]" : "bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A]"
        }`}
      >
        Join with {pkg.name.split(" ")[0]} <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function SuperProfilePage() {
  const [busy, setBusy] = useState(false);
  const onBuy = async (pkg) => {
    setBusy(true);
    await startCheckout({
      packageId: pkg.id,
      name: "Prospect",
      email: "prospect@skillpreneur.academy",
    });
    setBusy(false);
  };

  return (
    <main
      data-testid="super-profile-page"
      className="min-h-screen bg-[#F8FAFC] dark:bg-[#020617] text-[#1E293B] dark:text-[#F8FAFC]"
    >
      <Navigation />

      {/* HERO */}
      <section className="pt-32 pb-20 gradient-mesh">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200/80 dark:border-white/10 bg-white/70 dark:bg-white/5 glass px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-slate-700 dark:text-slate-200">
              <span className="h-1.5 w-1.5 rounded-full bg-[#06B6D4]" />
              The Super Profile Program
            </span>
            <h1
              data-testid="sp-title"
              className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-semibold text-[#0F172A] dark:text-white text-balance leading-[1.02]"
            >
              Learn High-Income Skills.
              <br />
              Build Your Personal Brand &amp;{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Start Earning Online.</span>
                <span className="absolute inset-x-0 bottom-2 h-3 bg-[#06B6D4]/40 -z-0 rounded" />
              </span>
            </h1>
            <p className="mt-7 text-lg leading-relaxed text-slate-600 dark:text-slate-300 max-w-2xl">
              The Super Profile Program is a complete skill-development
              ecosystem designed to help you build valuable digital skills,
              grow your personal brand, create opportunities and generate
              income online — all inside one premium experience.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href="#sp-packages"
                data-testid="sp-cta-join"
                className="inline-flex justify-center items-center gap-2 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-6 py-3.5 text-sm font-medium"
              >
                Join the Program <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#sp-courses"
                data-testid="sp-cta-courses"
                className="inline-flex justify-center items-center gap-2 rounded-full border border-slate-300 dark:border-white/15 px-6 py-3.5 text-sm font-medium text-[#0F172A] dark:text-white"
              >
                Explore Courses
              </a>
            </div>
          </motion.div>

          {/* Quick stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { v: "5", l: "Premium courses" },
              { v: "21+", l: "Benefits inside Premium" },
              { v: "1%", l: "Exclusive Club Access" },
              { v: "Lifetime", l: "Community + updates" },
            ].map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                className="rounded-3xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-6 hairline"
              >
                <div className="font-display text-3xl font-semibold tracking-tighter text-[#0F172A] dark:text-white">
                  {s.v}
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                  {s.l}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY THIS PROGRAM EXISTS */}
      <section data-testid="sp-why" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
              Why this program exists
            </span>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl tracking-tighter font-semibold text-[#0F172A] dark:text-white text-balance">
              The world rewards skills, brand, and execution.
            </h2>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              {
                t: "Skills companies pay for",
                d: "Real, modern, project-based learning — not theory.",
              },
              {
                t: "A brand that opens doors",
                d: "Be the obvious choice, not another resume in the pile.",
              },
              {
                t: "An ecosystem, not a course",
                d: "Community, mentors, accountability and continuous updates.",
              },
              {
                t: "Built for outcomes",
                d: "From learning → portfolio → clients → income.",
              },
            ].map((p) => (
              <div
                key={p.t}
                className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-6 hairline"
              >
                <Compass className="h-4 w-4 text-[#2563EB] dark:text-[#06B6D4]" />
                <div className="mt-3 font-display text-lg font-medium tracking-tight text-[#0F172A] dark:text-white">
                  {p.t}
                </div>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  {p.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section
        id="sp-roadmap"
        data-testid="sp-roadmap"
        className="py-20 sm:py-28 bg-gradient-to-b from-transparent to-slate-100/60 dark:to-white/[0.02]"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
              Complete Learning Roadmap
            </span>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl tracking-tighter font-semibold text-[#0F172A] dark:text-white text-balance">
              From your first skill to a scaling income.
            </h2>
          </div>

          <ol className="mt-14 grid gap-4 max-w-3xl">
            {ROADMAP.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.li
                  key={s.step}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  data-testid={`sp-roadmap-${i}`}
                  className="relative"
                >
                  <div className="flex gap-5 items-start">
                    <div className="flex-none flex flex-col items-center">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0F172A] text-white dark:bg-white dark:text-[#0F172A]">
                        <Icon className="h-5 w-5" />
                      </span>
                      {i < ROADMAP.length - 1 && (
                        <span className="block w-px flex-1 mt-3 mb-3 bg-gradient-to-b from-slate-300 dark:from-white/20 to-transparent min-h-12" />
                      )}
                    </div>
                    <div className="flex-1 pb-2">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#06B6D4] font-semibold">
                        {s.step}
                      </div>
                      <h3 className="mt-1 font-display text-2xl tracking-tight font-semibold text-[#0F172A] dark:text-white">
                        {s.title}
                      </h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-slate-600 dark:text-slate-300 max-w-xl">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* COURSES SHOWCASE */}
      <section id="sp-courses" data-testid="sp-courses" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div className="max-w-2xl">
              <span className="text-[11px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
                Courses inside the program
              </span>
              <h2 className="font-display mt-4 text-4xl sm:text-5xl tracking-tighter font-semibold text-[#0F172A] dark:text-white text-balance">
                5 premium courses. One ecosystem.
              </h2>
            </div>
            <a
              href="#sp-packages"
              data-testid="sp-courses-view-packages"
              className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-[#0F172A] dark:text-white"
            >
              See access options <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {COURSES_LIST.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.slug}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  data-testid={`sp-course-card-${c.slug}`}
                  className="group rounded-3xl overflow-hidden border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] hairline flex flex-col"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={c.cover}
                      alt={c.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/85 via-[#020617]/20 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-white font-semibold">
                        <Icon className="h-3 w-3" /> Mastery Course
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-5 right-5">
                      <h3 className="font-display text-xl tracking-tight font-semibold text-white">
                        {c.title}
                      </h3>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                      {c.short}
                    </p>
                    <ul className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                      <li className="flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 mt-1 text-[#06B6D4] flex-none" />
                        Project-driven curriculum
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 mt-1 text-[#06B6D4] flex-none" />
                        Mentor & community support
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-3.5 w-3.5 mt-1 text-[#06B6D4] flex-none" />
                        Lifetime access & updates
                      </li>
                    </ul>
                    <Link
                      to={`/courses/${c.slug}`}
                      data-testid={`sp-course-view-${c.slug}`}
                      className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#0F172A] dark:text-white"
                    >
                      View Course Details <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SKILLS COVERED + CAREERS */}
      <section data-testid="sp-skills-careers" className="py-20 sm:py-28 bg-gradient-to-b from-transparent to-slate-100/60 dark:to-white/[0.02]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10">
          <div className="rounded-3xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-8 sm:p-10 hairline">
            <Target className="h-5 w-5 text-[#2563EB] dark:text-[#06B6D4]" />
            <h3 className="mt-4 font-display text-3xl tracking-tight font-semibold text-[#0F172A] dark:text-white">
              Skills you'll cover
            </h3>
            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SKILLS.map((s) => (
                <li key={s} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <Check className="h-4 w-4 mt-0.5 text-[#06B6D4]" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-slate-200/80 dark:border-white/10 bg-[#0F172A] text-white p-8 sm:p-10 hairline relative overflow-hidden">
            <div className="absolute inset-0 gradient-mesh opacity-40 pointer-events-none" />
            <Trophy className="relative h-5 w-5 text-[#06B6D4]" />
            <h3 className="relative mt-4 font-display text-3xl tracking-tight font-semibold">
              Career opportunities
            </h3>
            <ul className="relative mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CAREERS.map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-slate-200">
                  <Check className="h-4 w-4 mt-0.5 text-[#06B6D4]" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PACKAGES (access levels) */}
      <section id="sp-packages" data-testid="sp-packages" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
              Choose your access level
            </span>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl tracking-tighter font-semibold text-[#0F172A] dark:text-white text-balance">
              You're not buying a course — you're joining the Super Profile Program.
            </h2>
            <p className="mt-5 text-slate-600 dark:text-slate-300">
              Pick the access level that fits your goals. Premium unlocks
              everything inside the ecosystem.
            </p>
          </div>
          <div className="mt-14 grid lg:grid-cols-3 gap-6 lg:items-start">
            {PACKAGES.map((p) => (
              <PackagePill key={p.id} pkg={p} onBuy={onBuy} />
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link
              to="/#comparison"
              data-testid="sp-compare-link"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F172A] dark:text-white"
            >
              Compare all benefits <ArrowDown className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section data-testid="sp-outcomes" className="py-20 sm:py-28 bg-gradient-to-b from-transparent to-slate-100/60 dark:to-white/[0.02]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="rounded-[2.5rem] border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-10 sm:p-14 hairline">
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
              Learning Outcomes
            </span>
            <h3 className="font-display mt-4 text-4xl tracking-tighter font-semibold text-[#0F172A] dark:text-white text-balance">
              By the end of the program you'll have…
            </h3>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {OUTCOMES.map((o) => (
                <li key={o} className="flex items-start gap-3 text-[15px] text-slate-700 dark:text-slate-200">
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-[#06B6D4] text-[#0F172A]">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {o}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROGRAM FAQ */}
      <section data-testid="sp-faq" className="py-24 sm:py-32">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <div className="text-center">
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
              Program FAQ
            </span>
            <h3 className="font-display mt-4 text-4xl tracking-tighter font-semibold text-[#0F172A] dark:text-white">
              Questions, answered.
            </h3>
          </div>
          <div className="mt-12 rounded-3xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] hairline overflow-hidden divide-y divide-slate-200/70 dark:divide-white/10">
            {PROGRAM_FAQS.map((f, i) => (
              <details key={f.q} data-testid={`sp-faq-${i}`} className="group">
                <summary className="cursor-pointer px-6 sm:px-8 py-6 list-none flex items-start gap-4 hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                  <span className="flex-1 font-display text-base sm:text-lg font-medium tracking-tight text-[#0F172A] dark:text-white">
                    {f.q}
                  </span>
                  <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 dark:border-white/15 text-xs text-slate-500 group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <div className="px-6 sm:px-8 pb-6 text-[15px] leading-relaxed text-slate-600 dark:text-slate-300">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <div className="rounded-[2.5rem] border border-slate-200/80 dark:border-white/10 bg-[#0F172A] text-white p-10 sm:p-14 relative overflow-hidden hairline">
            <div className="absolute inset-0 gradient-mesh opacity-40 pointer-events-none" />
            <span className="relative text-[11px] uppercase tracking-[0.24em] text-[#06B6D4] font-semibold">
              Ready to begin?
            </span>
            <h3 className="relative mt-4 font-display text-4xl sm:text-5xl tracking-tighter font-semibold text-balance">
              Join the Super Profile Program today.
            </h3>
            <p className="relative mt-5 text-slate-200 max-w-xl">
              Pick your access level and start with the skills, brand and
              community that will compound for years.
            </p>
            <div className="relative mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#sp-packages"
                data-testid="sp-final-cta"
                className="inline-flex justify-center items-center gap-2 rounded-full bg-white text-[#0F172A] px-6 py-3.5 text-sm font-medium"
              >
                Choose Your Access Level <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/"
                className="inline-flex justify-center items-center gap-2 rounded-full border border-white/20 text-white px-6 py-3.5 text-sm font-medium hover:bg-white/5"
              >
                Back to home
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
